import { Controller } from "@hotwired/stimulus"
import { CameraStream, focusDiagnosticsForTrack } from "../lib/camera_stream"
import { previewUrls } from "../lib/photo_preview"
import { logEvent } from "../field_logger"
import { getCameraCaptureMode } from "../lib/camera_capture_preference"
import { focusPointFromTap, showFocusRing } from "../lib/tap_to_focus"
import { pushForegroundArea, popForegroundArea } from "../field_foreground_time"
import { recordCameraOpened } from "../lib/camera_capture_session"
import { discardRemovedPhotoUrls } from "../lib/photo_removal"

// In-app fullscreen-feeling photo camera for SurveyJS photo questions.
//
// Singleton bottom-sheet rendered once on the response page. The result editor
// dispatches `field:photo-camera:open` with { question, multi } when the tasker
// taps a Take Photo button. We open the sheet, run a live getUserMedia stream
// (CameraStream), capture frames, wrap them as Files and feed them to SurveyJS
// via question.loadFiles([...]). That fires the EXISTING onUploadFiles pipeline
// (compress -> queue offline / upload + bank online), so SurveyJS owns the value
// shape and offline + My Photos keep working. The camera replaces only the INPUT
// mechanism, never the upload/value plumbing.
//
// Shares the bottom-sheet shell (field/shared/_camera_sheet) with the barcode
// scanner for identical skin -- different engine, same chrome.
//
// Native handoff (opt-in, device-detected default -- see
// camera_capture_preference.js): when getCameraCaptureMode() is "native",
// open() skips the sheet and CameraStream entirely and triggers a hidden
// native file input instead (the mechanism this controller used before card
// #1688, brought back gated behind the preference). The resulting File(s)
// still flow through the exact same loadFiles([...]) pipeline below.

export default class extends Controller {
  static targets = [
    "sheet", "overlay", "viewfinder", "status", "captureBtn", "doneBtn", "thumbs",
    "lensControls", "wideBtn", "oneXBtn", "twoXBtn", "nativeInput"
  ]

  // Soft cap when a multi-photo question doesn't author its own maxSize/cap.
  static DEFAULT_MULTI_CAP = 10

  // Single-photo questions keep exactly one shot -- the latest replaces it.
  static SINGLE_CAP = 1

  // Pause between the tap and the actual frame grab (card #2934). Continuous
  // autofocus on the live getUserMedia stream is always adjusting; a tap can
  // land mid-adjustment and produce a soft frame. We stay on frame-grab
  // (deliberate -- see camera_stream.js header) and instead give the lens a
  // moment to settle, with a "hold steady" status cue so the pause reads as
  // intentional rather than a stall.
  static CAPTURE_SETTLE_DELAY_MS = 250

  connect() {
    this.sheetTarget.style.transform = "translateY(100%)"
    this.overlayTarget.style.opacity = "0"
    this.overlayTarget.style.pointerEvents = "none"
    this.overlayTarget.style.backgroundColor = "rgba(0,0,0,0.6)"
    requestAnimationFrame(() => {
      this.sheetTarget.style.transition = "transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)"
      this.overlayTarget.style.transition = "opacity 0.25s ease"
    })

    this._camera = null
    this._question = null
    this._multi = false
    // The strip represents the COMPLETE set for the question. Each entry is one
    // of two kinds:
    //   - { kind: "existing", value, url } -- a photo already committed to the
    //     question value (carries its original SurveyJS value object so Done can
    //     keep it verbatim).
    //   - { kind: "new", file, url } -- a freshly captured shot (carries the File
    //     to upload through the normal loadFiles pipeline on Done).
    this._captures = []
    this._objectUrls = []    // every object URL created, revoked on close
    this._mode = "1x"        // current discrete lens mode: "wide" | "1x" | "2x"
    // Card #4838 -- "in-app camera" foreground-time area override, only ever
    // pushed once the in-app sheet's own stream is actually live (see open()).
    // Native handoff hands the OS camera the foreground itself (this page
    // goes hidden via the normal visibilitychange path), so it never pushes.
    this._areaPushed = false

    this._openHandler = (e) => this.open(e)
    window.addEventListener("field:photo-camera:open", this._openHandler)

    // Card #4368 -- the question photo strip (photo_preview.js) renders beneath
    // the SurveyJS question, outside this element's pwa-image-viewer scope
    // (this div only wraps the camera sheet), so it hands off via a window
    // event rather than a click action. We're the only other controller on
    // this element, so we forward straight to our co-located viewer instance.
    this._viewerOpenHandler = (e) => this._openViewer(e)
    window.addEventListener("field:photo-viewer:open", this._viewerOpenHandler)
  }

  disconnect() {
    window.removeEventListener("field:photo-camera:open", this._openHandler)
    window.removeEventListener("field:photo-viewer:open", this._viewerOpenHandler)
    // Card #4838 -- a Turbo navigation away while the sheet is still open
    // (no close() in between) must not leave "camera" stuck on the
    // foreground-time area override stack forever.
    if (this._areaPushed) {
      popForegroundArea()
      this._areaPushed = false
    }
    this._camera?.stop()
    this._revokeUrls()
  }

  // Forwards a field:photo-viewer:open handoff to the pwa-image-viewer
  // controller sharing this element (see field/responses/show.html.erb).
  _openViewer(event) {
    const { urls, index, onDelete } = event.detail || {}
    const viewer = this.application.getControllerForElementAndIdentifier(this.element, "pwa-image-viewer")
    viewer?.openUrls(urls, index, { onDelete })
  }

  // Opens the sheet for a given question or in standalone mode (question=null).
  // detail: { question, multi }. When question is absent the sheet runs
  // standalone: multi-capture by default, files dispatched on Done via
  // field:photo-camera:files rather than fed back to SurveyJS.
  //
  // Native handoff check comes first: when the tasker's preference (device
  // default or explicit override) is "native", we never touch the sheet or
  // CameraStream at all -- see _openNative.
  async open(event) {
    const { question, multi } = event.detail || {}

    this._openedAt = performance.now()

    if (getCameraCaptureMode() === "native") {
      this._logCameraOpen("native", { pillVisible: false, pillReadyMs: null })
      this._openNative(question, multi)
      return
    }

    this._standalone = !question
    this._question = question || null
    this._multi = this._standalone ? true : !!multi
    this._captures = question ? this._seedExistingEntries(question) : []
    this._mode = "1x"
    this._renderThumbs()
    this._hideHardwareControls()

    this.overlayTarget.style.pointerEvents = "auto"
    this.overlayTarget.style.opacity = "1"
    this.sheetTarget.style.transform = "translateY(0)"
    this._toggleStripControls()
    this._setStatus("Take photos, then tap Done")

    this._camera = new CameraStream()
    try {
      await this._camera.start(this.viewfinderTarget, { facingMode: "environment" })
      await this._syncHardwareControls()
      this._logCameraOpen("camera", {
        pillVisible: this._pillVisible(),
        pillReadyMs: this._pillReadyMs,
        activeMode: this._mode,
        startDiagnostics: this._camera.startDiagnostics()
      })
    } catch (err) {
      this._handleStreamFailure(err)
    }
  }

  // Native handoff (opt-in): skip the sheet and CameraStream entirely, and
  // trigger the hidden native file input instead. Reuses the same
  // _question/_multi/_standalone bookkeeping the in-app path sets so
  // _capForQuestion() and _loadIntoQuestion() behave identically -- the only
  // difference is WHERE the File(s) come from. Nothing here builds or opens
  // the sheet, so none of its visual state changes.
  _openNative(question, multi) {
    this._standalone = !question
    this._question = question || null
    this._multi = this._standalone ? true : !!multi

    // Multi-photo questions ask the native picker for more than one shot
    // where the platform honours it -- some platforms only ever return one
    // per invocation regardless (an accepted platform limitation).
    const cap = this._capForQuestion()
    this.nativeInputTarget.multiple = this._multi && cap > 1
    this.nativeInputTarget.value = ""
    this.nativeInputTarget.click()
  }

  // Fired when the tasker picks/shoots file(s) via the native handoff input.
  // The resulting File(s) flow through the EXACT same pipeline as an in-app
  // capture: question.loadFiles([...]) for a question, or the
  // field:photo-camera:files event for the standalone (My Photos) surface --
  // no change to compression, queueing, or upload either way.
  nativeFilesSelected() {
    const files = Array.from(this.nativeInputTarget.files || [])
    this.nativeInputTarget.value = ""
    if (!files.length) return

    files.forEach((file) => this._logNativeFrameCaptured(file))

    if (this._standalone) {
      window.dispatchEvent(new CustomEvent("field:photo-camera:files", { detail: { files } }))
    } else {
      this._loadIntoQuestion(files)
    }
    this._question = null
    this._standalone = false
  }

  // Native handoff bypasses CameraStream entirely, so there's no frame to read
  // dimensions off directly -- decode the picked File instead, the same
  // createImageBitmap approach field_image_compressor.js uses for gallery
  // uploads. Fire-and-forget from the caller: never awaited, so it can't delay
  // loadFiles/the files event above. A decode failure still logs the event
  // with null dimensions rather than dropping it silently -- same
  // honest-degrade principle as the rest of this file's telemetry.
  async _logNativeFrameCaptured(file) {
    let width = null
    let height = null
    try {
      const bitmap = await createImageBitmap(file)
      width = bitmap.width
      height = bitmap.height
      bitmap.close()
    } catch {
      // Decode failed -- log the event anyway with null dimensions.
    }
    logEvent("camera", "Frame captured", { width, height, method: "native", size: file.size }).catch(() => {})
  }

  // Capture a frame. Both modes hold the shot in the strip; Done commits.
  // Single keeps exactly one (latest replaces previous); multi appends up to
  // the question's cap. The sheet stays open in both modes so the tasker can
  // re-shoot freely -- nothing persists until Done.
  //
  // A short settle delay (CAPTURE_SETTLE_DELAY_MS) sits between the tap and
  // the actual frame grab, with a "hold steady" status cue -- see the
  // constant's doc for why. The shutter AND Done are disabled for the
  // duration: disabled controls don't receive click events at all, so a
  // second tap during the pause is a no-op rather than stacking captures,
  // and Done can't commit before a just-tapped capture has finished settling.
  async capture() {
    if (!this._camera) return
    this._setCaptureBusy(true)
    this._setStatus("Hold steady…")

    await this._wait(this.constructor.CAPTURE_SETTLE_DELAY_MS)

    if (!this._camera) {
      // Sheet was closed mid-settle -- nothing left to capture.
      this._setCaptureBusy(false)
      return
    }

    let blob
    try {
      blob = await this._camera.captureFrame({ aspect: this._viewfinderAspect() })
      const dimensions = this._camera.frameDimensions()
      logEvent("camera", "Frame captured", {
        width: dimensions?.width || null,
        height: dimensions?.height || null,
        mode: this._mode,
        method: this._camera.captureMethod()
      }).catch(() => {})
      // Real still capture attempted but takePhoto() itself threw (card
      // #3487) -- captureFrame() already fell back to the video-frame path
      // silently, this just makes the fallback visible in app_logs.
      const fallbackReason = this._camera.stillCaptureFallbackReason()
      if (fallbackReason) {
        logEvent("camera", "Still capture fell back to video frame", { reason: fallbackReason }).catch(() => {})
      }
    } catch (err) {
      this._setStatus("Couldn't capture — try again")
      logEvent("camera", "Frame capture failed", { code: err?.code || "unknown" }).catch(() => {})
      return
    } finally {
      this._setCaptureBusy(false)
    }

    const file = this._blobToFile(blob)
    const url = URL.createObjectURL(blob)
    this._objectUrls.push(url)

    if (!this._multi) {
      // Single: the latest capture REPLACES whatever the strip held (existing or
      // new), keeping exactly one entry total.
      this._captures = [{ kind: "new", file, url }]
      this._renderThumbs()
      this._setStatus("Photo ready — tap Done")
      return
    }

    // Multi: append up to the cap (existing + new entries both count).
    const cap = this._capForQuestion()
    if (this._captures.length >= cap) {
      this._setStatus(`Maximum of ${cap} reached`)
      return
    }
    this._captures.push({ kind: "new", file, url })
    this._renderThumbs()
    this._setStatus(`${this._captures.length} photo${this._captures.length === 1 ? "" : "s"} ready`)
  }

  // Commit the strip to SurveyJS. The strip is the COMPLETE intended set, so we
  // reconcile in two steps:
  //   1. Set the question value to the KEPT-EXISTING entries only (their original
  //      value objects, in strip order). This persists any removal of a
  //      previously-committed photo -- the dropped ones simply aren't in the set.
  //   2. loadFiles the NEW captures, which APPENDS them through the existing
  //      onUploadFiles pipeline (compress -> queue offline / upload + bank online).
  // Net effect: existing-kept retain their value verbatim, removals drop, new
  // captures upload and append. Add-only is the common case and stays a plain
  // append; the reconcile only does extra work when an existing photo was removed.
  done() {
    const kept = this._captures.filter((c) => c.kind === "existing")
    const fresh = this._captures.filter((c) => c.kind === "new")

    if (this._standalone) {
      if (fresh.length) {
        window.dispatchEvent(new CustomEvent("field:photo-camera:files", {
          detail: { files: fresh.map((c) => c.file) }
        }))
      }
      this.close()
      return
    }

    // Only rewrite the value when an existing photo was removed -- otherwise the
    // existing set is unchanged and loadFiles' own append handles everything,
    // matching the prior add-only contract exactly.
    if (this._existingWasRemoved()) {
      this._setQuestionValue(kept.map((c) => c.value))

      // Card #4744 (the LOCAL COPY GAP fix) -- a removal here used to stop at
      // the value rewrite above and never discard the dropped photo's queued
      // copy (or its server Image, if it had already landed). Same cleanup
      // every other removal surface gets, via the one shared function.
      const keptUrls = new Set(kept.map((c) => c.url))
      const droppedUrls = (this._seededExistingUrls || []).filter((url) => !keptUrls.has(url))
      if (droppedUrls.length) {
        const csrfToken = document.querySelector("[name='csrf-token']")?.content
        discardRemovedPhotoUrls(droppedUrls, { csrfToken }).catch(() => {})
      }
    }

    if (fresh.length) {
      this._loadIntoQuestion(fresh.map((c) => c.file))
    }
    this.close()
  }

  removeThumb(event) {
    const index = Number(event.params?.index ?? event.currentTarget?.dataset?.index)
    if (Number.isNaN(index)) return
    const [removed] = this._captures.splice(index, 1)
    // Only revoke object URLs we created (new captures). Existing entries carry a
    // server/blob URL owned by SurveyJS -- leave it alone.
    if (removed?.kind === "new" && removed.url) URL.revokeObjectURL(removed.url)
    this._renderThumbs()
    this._setStatus(`${this._captures.length} photo${this._captures.length === 1 ? "" : "s"} ready`)
  }

  async flipCamera() {
    if (!this._camera) return
    this._camera.flip()
    this._mode = "1x"
    try {
      await this._camera.start(this.viewfinderTarget, { facingMode: this._camera.facingMode })
      await this._syncHardwareControls()
    } catch (err) {
      this._handleStreamFailure(err)
    }
  }

  // Discrete lens mode -- the single segmented control. Reads data-mode
  // ("wide" | "1x" | "2x") off the tapped button and asks CameraStream to switch.
  // Restarts the stream cleanly into the same viewfinder, re-applies the
  // WYSIWYG framing, and reflects the active segment.
  //
  // A tap here only updates this._mode -- it holds for as long as the sheet
  // stays open (nothing resets it mid-session) and goes back to 1x the next
  // time open() runs (card #4805: the sheet must always open on 1x, never a
  // lens remembered from an earlier sheet).
  async setMode(event) {
    if (!this._camera) return
    const requested = event.currentTarget?.dataset?.mode
    if (!requested || requested === this._mode) return
    try {
      this._mode = await this._camera.selectMode(this.viewfinderTarget, requested)
      this._updateLensControls()
    } catch (err) {
      this._handleStreamFailure(err)
    }
  }

  close() {
    if (this._areaPushed) {
      popForegroundArea()
      this._areaPushed = false
    }
    this._camera?.stop()
    this._camera = null
    this._setCaptureBusy(false)
    this.overlayTarget.style.opacity = "0"
    this.overlayTarget.style.pointerEvents = "none"
    this.sheetTarget.style.transform = "translateY(100%)"
    this._revokeUrls()
    this._captures = []
    this._seededExistingCount = 0
    this._seededExistingUrls = []
    this._mode = "1x"
    this._standalone = false
    this._renderThumbs()
    this._hideHardwareControls()
    if (this.hasDoneBtnTarget) this.doneBtnTarget.classList.add("hidden")
    if (this.hasThumbsTarget) this.thumbsTarget.classList.add("hidden")
    this._question = null
  }

  // ── private ──────────────────────────────────────────────────────────────

  // After a stream starts, enumerate the back cameras (labels need an active
  // grant) and probe the active track to decide which of Wide | 1x | 2x to show.
  // 1x is always available; Wide and 2x are capability-gated. If only 1x would
  // be available, the whole segmented control stays hidden (no single dead
  // segment). Also emits the camera diagnostic so a mis-detected lens can be
  // corrected from app logs (device-dependent, can't be verified headlessly).
  async _syncHardwareControls() {
    if (!this._camera) return

    await this._camera.refreshBackCameras()
    this._logCameraDiagnostic()

    const wideOk = this._camera.supportsWide()
    const twoXOk = this._camera.supportsTwoX()

    if (this.hasWideBtnTarget) this.wideBtnTarget.classList.toggle("hidden", !wideOk)
    if (this.hasTwoXBtnTarget) this.twoXBtnTarget.classList.toggle("hidden", !twoXOk)

    // Show the control only when there's a real choice beyond bare 1x. The pill
    // is toggled with `invisible` (not `hidden`) so its space is reserved on
    // every open and it never pops in and out of the layout (card #4110).
    if (this.hasLensControlsTarget) {
      this.lensControlsTarget.classList.toggle("invisible", !(wideOk || twoXOk))
    }
    this._pillReadyMs = this._openedAt != null ? Math.round(performance.now() - this._openedAt) : null
    this._updateLensControls()

    // Tap-to-focus (card #3488). Always wired once the track is live: phones
    // with manual focus get it, the rest get the ring plus an autofocus nudge.
    this._toggleFocusTap(!!this._camera._track())
    this._logFocusCapabilities()
  }

  // One "Focus capabilities" event per camera open: what the track advertises
  // and reports for focus, plus which lens is live, so a phone that won't
  // tap-focus can be diagnosed from the app log. A wrong close-focus lens shows
  // up as activeIsWide true at 1x. No PII (hardware label and short lens id).
  _logFocusCapabilities() {
    try {
      const track = this._camera._track()
      if (!track) return
      const selection = this._camera.wideSelection()
      const activeDeviceId = track.getSettings?.().deviceId || null
      logEvent("camera", "Focus capabilities", {
        ...focusDiagnosticsForTrack(track),
        lensMode: this._mode,
        activeLensId: this._shortId(activeDeviceId),
        activeIsWide: !!(selection?.deviceId && activeDeviceId && selection.deviceId === activeDeviceId),
        wideLabel: selection?.label || null
      }).catch(() => {})
    } catch {
      // Diagnostics never block the camera.
    }
  }

  // Fire-and-forget diagnostic: list each back camera's short deviceId hash +
  // label and which one we chose as Wide, so a wrong heuristic on a specific
  // Android can be hard-corrected from the field logs.
  _logCameraDiagnostic() {
    try {
      const selection = this._camera.wideSelection()
      const wideId = selection?.deviceId || null
      const cameras = this._camera.backCameras().map((d) => ({
        id: this._shortId(d.deviceId),
        label: d.label || "(no label)",
        chosenWide: d.deviceId === wideId
      }))
      logEvent("camera", "Back cameras enumerated", {
        count: cameras.length,
        cameras,
        wideChosen: wideId ? this._shortId(wideId) : null,
        wideLabel: selection?.label || null,
        wideReason: selection?.reason || null
      }).catch(() => {})
    } catch {
      // Diagnostics never block the camera.
    }
  }

  // A short, log-friendly hash of a deviceId (which can be long and opaque).
  _shortId(deviceId) {
    if (!deviceId) return null
    let hash = 0
    for (let i = 0; i < deviceId.length; i++) {
      hash = (hash * 31 + deviceId.charCodeAt(i)) | 0
    }
    return (hash >>> 0).toString(16).slice(0, 8)
  }

  // Hide the lens control group (used on open/close before probing).
  _hideHardwareControls() {
    // `invisible` keeps the pill's footprint (all three segments rendered) so
    // nothing shifts when the stream is ready. It is inert until then.
    if (this.hasLensControlsTarget) this.lensControlsTarget.classList.add("invisible")
    if (this.hasWideBtnTarget) this.wideBtnTarget.classList.remove("hidden")
    if (this.hasTwoXBtnTarget) this.twoXBtnTarget.classList.remove("hidden")
    this._toggleFocusTap(false)
  }

  // True when the lens pill is showing to the tasker (not the reserved-space
  // invisible state).
  _pillVisible() {
    return this.hasLensControlsTarget && !this.lensControlsTarget.classList.contains("invisible")
  }

  // One diagnostics event per camera open (card #4110): which capture path ran,
  // device posture, how long the pill took to be ready, whether it ended up
  // visible, and which camera label was chosen as Wide. No PII.
  //
  // Card #4838: also starts the camera-open -> capture-returned clock
  // (camera_capture_session.js) so the eventual "Photo capture returned"
  // event can carry cameraMs + cameraMode. captureMode "native" maps to
  // cameraMode "native"; "camera" and the "photo-picker-fallback" rescue path
  // both map to "in-app" -- both start from the in-app sheet.
  //
  // Also pushes the "in-app camera" foreground-time area override, but only
  // for "camera" (the sheet's own stream is actually live and on screen).
  // Native handoff backgrounds this page itself (hidden via the normal
  // visibilitychange path); the fallback path closes the sheet immediately
  // after, so pushing there would just be popped straight back off.
  _logCameraOpen(captureMode, extra = {}) {
    try {
      recordCameraOpened(captureMode === "native" ? "native" : "in-app")
      if (captureMode === "camera") {
        pushForegroundArea("camera")
        this._areaPushed = true
      }
      const selection = this._camera?.wideSelection?.() || null
      logEvent("camera", "Camera opened", {
        captureMode,
        orientation: this._orientation(),
        standalone: this._isStandalone(),
        pillReadyMs: null,
        pillVisible: false,
        wideLabel: selection?.label || null,
        wideReason: selection?.reason || null,
        ...extra
      }).catch(() => {})
    } catch {
      // Diagnostics never block the camera.
    }
  }

  _orientation() {
    try {
      return window.screen?.orientation?.type ||
        (window.matchMedia?.("(orientation: landscape)").matches ? "landscape" : "portrait")
    } catch {
      return null
    }
  }

  _isStandalone() {
    try {
      return !!(window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone)
    } catch {
      return false
    }
  }

  // ── Tap-to-focus (card #3488) ────────────────────────────────────────────

  // Wire up (or tear down) the viewfinder's tap listener. Attached whenever a
  // track is live. Without manual-focus support (iOS, some Android) the tap
  // falls back to the ring plus a best-effort continuous-autofocus nudge.
  _toggleFocusTap(enabled) {
    if (!this.hasViewfinderTarget) return
    if (enabled && !this._focusTapHandler) {
      this._focusTapHandler = (event) => this._onViewfinderTap(event)
      this.viewfinderTarget.addEventListener("click", this._focusTapHandler)
    } else if (!enabled && this._focusTapHandler) {
      this.viewfinderTarget.removeEventListener("click", this._focusTapHandler)
      this._focusTapHandler = null
    }
  }

  // Tap handler: translate the tap into a focus point, apply it, and show a
  // brief focus-ring indicator at the tap location (standard camera-app
  // convention). Best-effort -- a failed applyConstraints still shows the ring
  // (the tasker tapped a real point; whether the hardware honoured it is a
  // hardware concern, not a UI one) but is logged either way.
  //
  // The coordinate maths and ring visual are shared with
  // field_odometer_camera_controller.js via lib/tap_to_focus.js (card #4495)
  // -- both surfaces render a <video> under object-fit: cover and need the
  // identical tap-to-native-frame translation.
  _onViewfinderTap(event) {
    if (!this._camera?._track()) return
    const point = focusPointFromTap(this.viewfinderTarget, event.clientX, event.clientY)
    if (!point) return

    showFocusRing(this.viewfinderTarget.parentElement, event.clientX, event.clientY)
    this._camera.focusOrNudge(point.x, point.y).then(({ mode, success, confirmed, attempts }) => {
      logEvent("camera", "Tap-to-focus applied", { x: point.x, y: point.y, success, mode, confirmed, attempts, lensMode: this._mode }).catch(() => {})
    })
  }

  // Reflect the active mode in the segmented control. Active segment is a solid
  // white pill with dark text; inactive segments are translucent white text so
  // they read against the dark camera scrim.
  _updateLensControls() {
    const set = (btn, on) => {
      if (!btn) return
      btn.classList.toggle("bg-white", on)
      btn.classList.toggle("shadow-sm", on)
      btn.classList.toggle("text-slate-800", on)
      btn.classList.toggle("text-white/70", !on)
    }
    set(this.hasWideBtnTarget ? this.wideBtnTarget : null, this._mode === "wide")
    set(this.hasOneXBtnTarget ? this.oneXBtnTarget : null, this._mode === "1x")
    set(this.hasTwoXBtnTarget ? this.twoXBtnTarget : null, this._mode === "2x")
  }

  // Seed the strip from the question's CURRENT value so reopening shows the
  // photos already captured for this question. Each existing photo becomes an
  // { kind: "existing", value, url } entry, carrying its original SurveyJS value
  // object so Done can keep it verbatim. The matching displayable URLs come from
  // previewUrls (the same extraction the on-question preview uses), zipped back
  // to the raw value entries 1:1. Records the seeded count so _existingWasRemoved
  // can tell whether the tasker dropped any of them.
  _seedExistingEntries(question) {
    const value = question?.value
    const raw = Array.isArray(value) ? value : []
    const urls = previewUrls(value)
    const entries = raw
      .map((v, i) => ({ kind: "existing", value: v, url: urls[i] }))
      .filter((e) => e.url)
    this._seededExistingCount = entries.length
    // Full url list at seed time, for diffing against what's left at Done
    // (card #4744 -- the LOCAL COPY GAP fix: knowing a photo was dropped
    // isn't enough, discarding its queued copy needs the actual url).
    this._seededExistingUrls = entries.map((e) => e.url)
    return entries
  }

  // True when the strip now holds FEWER existing entries than it was seeded with
  // -- i.e. the tasker removed a previously-committed photo, so Done must rewrite
  // the value rather than blindly append.
  _existingWasRemoved() {
    const stillThere = this._captures.filter((c) => c.kind === "existing").length
    return stillThere < (this._seededExistingCount || 0)
  }

  // Set the question value directly to the given array of original value objects
  // (kept-existing only). Used to persist removals before new captures append.
  _setQuestionValue(values) {
    try {
      this._question.value = values
    } catch (err) {
      logEvent("upload", "set question value failed", { error: String(err) }).catch(() => {})
    }
  }

  // Hand Files to SurveyJS. loadFiles fires onUploadFiles (the existing pipeline).
  _loadIntoQuestion(files) {
    try {
      this._question.loadFiles(files)
    } catch (err) {
      logEvent("upload", "loadFiles failed", { error: String(err) }).catch(() => {})
    }
  }

  // getUserMedia denied / unavailable -- reveal SurveyJS's native file input so
  // the tasker is never stranded, and log telemetry on the fallback (step 5).
  _handleStreamFailure(err) {
    const code = err?.code || "unknown"
    this._setStatus("Camera unavailable — use the file picker")
    logEvent("camera", "getUserMedia fallback to native input", {
      code,
      questionName: this._question?.name || null,
      multi: this._multi,
      captureMode: "photo-picker-fallback",
      wideAvailable: false
    }).catch(() => {})
    this._logCameraOpen("photo-picker-fallback", { pillVisible: false, pillReadyMs: null, errorCode: code })

    if (!this._standalone) this._revealNativeInput(code)
    this.close()
  }

  // Surface the SurveyJS native file input that the editor hid for this question.
  // "NotAllowedError" specifically means the tasker denied/never-granted camera
  // permission -- that's the one case where an inline explanation helps (other
  // codes like NotReadableError/OverconstrainedError/insecure-context are
  // hardware/environment issues, not something Settings can fix, so they keep
  // the silent fallback).
  _revealNativeInput(code) {
    const name = this._question?.name
    if (!name) return
    const root = document.querySelector(`[data-name="${name}"]`)
    if (!root) return
    root.querySelectorAll("[data-photo-camera-hidden]").forEach((el) => {
      el.style.display = ""
      el.removeAttribute("data-photo-camera-hidden")
    })
    const trigger = root.querySelector('[data-photo-camera-target="trigger"]')
    if (trigger) trigger.style.display = "none"

    if (code === "NotAllowedError") this._insertPermissionNotice(root)
  }

  // Persistent inline notice explaining the silent-looking fallback, inserted
  // right alongside the revealed native file picker. Guards against
  // double-inserting if the tasker retries and hits the same denial again for
  // this question (mirrors the trigger-button double-injection guard in
  // surveys/result_editor_controller.js).
  _insertPermissionNotice(root) {
    if (root.querySelector("[data-photo-camera-fallback-notice]")) return

    const notice = document.createElement("div")
    notice.setAttribute("data-photo-camera-fallback-notice", "")
    notice.className = "mt-2 mb-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
    notice.innerHTML = `Camera permission is off for this app — using your phone's photo picker instead. <a href="/field/more" class="underline font-medium">Tap here to fix it</a>`

    const decorator = root.querySelector(".sd-file__decorator") || root.querySelector('input[type="file"]')
    if (decorator) {
      decorator.insertAdjacentElement("afterend", notice)
    } else {
      root.appendChild(notice)
    }
  }

  // Disable the shutter and Done while a capture is settling or in flight.
  // Disabled controls receive no click events, so this is the actual guard
  // against a stacked second tap (not just a dimmed hint) and against Done
  // committing before a just-tapped capture has landed in the strip.
  _setCaptureBusy(busy) {
    if (this.hasCaptureBtnTarget) {
      this.captureBtnTarget.disabled = busy
      this.captureBtnTarget.classList.toggle("opacity-60", busy)
    }
    if (this.hasDoneBtnTarget) this.doneBtnTarget.disabled = busy
  }

  // Promise-based pause used to settle the lens before grabbing a frame.
  _wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  _capForQuestion() {
    if (!this._multi) return this.constructor.SINGLE_CAP
    if (this._standalone) return this.constructor.DEFAULT_MULTI_CAP
    // Honour an authored maxSize cap if the question expresses a file count;
    // SurveyJS maxSize is a byte limit, so the real per-question cap lives in
    // the question's own constraints. Default soft cap otherwise (never crash).
    const authored = Number(this._question?.maxCount || this._question?.maxFileCount)
    if (Number.isFinite(authored) && authored > 0) return authored
    return this.constructor.DEFAULT_MULTI_CAP
  }

  // The displayed aspect (width / height) of the rendered viewfinder frame, so
  // the capture crop matches exactly what the tasker sees. Falls back to the
  // video element's own box when the wrapper can't be measured.
  _viewfinderAspect() {
    const video = this.hasViewfinderTarget ? this.viewfinderTarget : null
    if (!video) return null
    const frame = video.parentElement || video
    const w = frame.clientWidth || video.clientWidth
    const h = frame.clientHeight || video.clientHeight
    return w > 0 && h > 0 ? w / h : null
  }

  _blobToFile(blob) {
    const ext = blob.type === "image/jpeg" ? "jpg" : "webp"
    const name = `capture_${Date.now()}.${ext}`
    return new File([blob], name, { type: blob.type, lastModified: Date.now() })
  }

  _renderThumbs() {
    if (!this.hasThumbsTarget) return
    this.thumbsTarget.innerHTML = ""
    // Fix 1: a single thumbnail centres horizontally; 2+ lay out from the start
    // and wrap (Fix 3). Toggle justify-center only when exactly one entry.
    this.thumbsTarget.classList.toggle("justify-center", this._captures.length === 1)
    this._captures.forEach((cap, index) => {
      const wrap = document.createElement("div")
      wrap.className = "relative shrink-0"

      const img = document.createElement("img")
      img.src = cap.url
      // Square-ish thumb, cover-cropped -- frame shape varies per device now.
      // White ring so the thumb reads against the dark camera scrim.
      img.className = "h-16 w-16 rounded-lg object-cover ring-2 ring-white/80 cursor-pointer"
      img.dataset.action = "click->pwa-image-viewer#open"
      img.dataset.pwaImageViewerUrlParam = cap.url
      wrap.appendChild(img)

      const remove = document.createElement("button")
      remove.type = "button"
      remove.className = "absolute -top-1.5 -right-1.5 size-5 rounded-full bg-slate-800 text-white flex items-center justify-center shadow"
      remove.setAttribute("data-action", "click->field-photo-camera#removeThumb")
      remove.setAttribute("data-field-photo-camera-index-param", String(index))
      remove.dataset.index = String(index)
      remove.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>'
      wrap.appendChild(remove)

      this.thumbsTarget.appendChild(wrap)
    })
  }

  // Done + strip now show in BOTH modes -- single keeps one, multi keeps many.
  _toggleStripControls() {
    if (this.hasDoneBtnTarget) this.doneBtnTarget.classList.remove("hidden")
    if (this.hasThumbsTarget) this.thumbsTarget.classList.remove("hidden")
  }

  _revokeUrls() {
    this._objectUrls.forEach((url) => URL.revokeObjectURL(url))
    this._objectUrls = []
  }

  _setStatus(msg) {
    if (this.hasStatusTarget) this.statusTarget.textContent = msg
  }
}
